import { Camera, Upload } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useScreenLogging } from '../../../hooks/useScreenLogging';
import { getSites } from '../../Inventory/inventoryOnboardingApi';
import { Button, Card } from '../../ui';
import PrimaryButton from '../../ui/PrimaryButton';
import Input from '../../ui/input';
import MultiSelectDialog from '../MultiSelectDialog';
import { customerApi } from './customerApi';

const createEmptyPoc = () => ({
  name: '',
  role: '',
  mobileNo: '',
  email: '',
});

const CustomerOnboarding = () => {
  // Log screen view
  useScreenLogging('CustomerOnboarding', {
    component: 'CustomerOnboarding',
    screenType: 'customer-onboarding',
  });

  const [customerName, setCustomerName] = useState('');
  const [billingAddress, setBillingAddress] = useState({
    email: '',
    address: '',
    country: '',
    pinCode: '',
    city: '',
    state: '',
  });
  const [bankDetails, setBankDetails] = useState({
    panNumber: '',
    gstNumber: '',
    bankName: '',
    branchAddress: '',
    accountNo: '',
    ifscNo: '',
  });
  const [pocDetails, setPocDetails] = useState({
    poc1: createEmptyPoc(),
    poc2: createEmptyPoc(),
  });
  const [sites, setSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [siteError, setSiteError] = useState('');
  const [showSiteDialog, setShowSiteDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ success: '', error: '' });
  const [logoData, setLogoData] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [hasLogo, setHasLogo] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchSites = async () => {
      setIsLoadingSites(true);
      setSiteError('');
      try {
        const siteList = await getSites();
        // Sort sites alphabetically by site_name or name
        const sortedSites = [...(siteList || [])].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );
        setSites(sortedSites);
      } catch (error) {
        setSiteError('Unable to load sites. Please try again later.');
      } finally {
        setIsLoadingSites(false);
      }
    };

    fetchSites();
  }, []);

  // Cleanup logo preview URL on unmount
  useEffect(() => {
    return () => {
      if (logoPreview && logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const handleBillingAddressChange = (field, value) => {
    setBillingAddress((prev) => ({
      ...prev,
      [field]: value,
    }));
    setFeedback({ success: '', error: '' });
  };

  const handleBankDetailsChange = (field, value) => {
    setBankDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
    setFeedback({ success: '', error: '' });
  };


  const handlePocChange = (pocKey, field, value) => {
    setPocDetails((prev) => ({
      ...prev,
      [pocKey]: {
        ...prev[pocKey],
        [field]: value,
      },
    }));
    setFeedback({ success: '', error: '' });
  };

  const isBillingAddressComplete = () =>
    !!billingAddress.email &&
    !!billingAddress.address &&
    !!billingAddress.country &&
    !!billingAddress.pinCode &&
    !!billingAddress.city &&
    !!billingAddress.state;

  const isBankDetailsComplete = () =>
    !!bankDetails.panNumber &&
    !!bankDetails.gstNumber &&
    !!bankDetails.bankName &&
    !!bankDetails.branchAddress &&
    !!bankDetails.accountNo &&
    !!bankDetails.ifscNo;

  const hasSelectedSites = selectedSites.length > 0;

  const isPocComplete = () => {
    // Only POC1 (primary_contact) is required
    // POC2 (secondary_contact) is optional
    const poc1 = pocDetails.poc1;
    return !!(poc1.name && poc1.role && poc1.mobileNo && poc1.email);
  };

  const isFormValid =
    !!customerName.trim() &&
    hasSelectedSites &&
    isBillingAddressComplete() &&
    isBankDetailsComplete() &&
    isPocComplete();

  // Debug logging in development
  if (process.env.NODE_ENV === 'development') {
    console.log('Form validation:', {
      customerName: !!customerName.trim(),
      hasSelectedSites,
      isBillingAddressComplete: isBillingAddressComplete(),
      isBankDetailsComplete: isBankDetailsComplete(),
      isPocComplete: isPocComplete(),
      isFormValid,
    });
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isFormValid) {
      setFeedback({ success: '', error: 'Please fill all required fields.' });
      return;
    }

    setIsSubmitting(true);
    setFeedback({ success: '', error: '' });
    
    try {
      // Map form data to API payload structure
      const payload = {
        customer_name: customerName.trim(),
        status: 'active',
        gst_number: bankDetails.gstNumber || undefined,
        pan_number: bankDetails.panNumber || undefined,
        contact_email: billingAddress.email || undefined,
        contact_phone: undefined, // Not in form
        contact_mobile: undefined, // Not in form
        contact_person: pocDetails.poc1?.name || undefined,
        payment_terms: undefined, // Not in form
        notes: undefined, // Not in form
        site_ids: selectedSites.map((site) => site.site_id || site.id),
        site_name: selectedSites.length > 0 ? selectedSites[0].site_name : undefined,
        billing_address: {
          street: billingAddress.address || undefined,
          city: billingAddress.city || undefined,
          state: billingAddress.state || undefined,
          pincode: billingAddress.pinCode || undefined,
          country: billingAddress.country || undefined,
        },
        shipping_address: {
          // Using billing address as shipping address if not provided separately
          street: billingAddress.address || undefined,
          city: billingAddress.city || undefined,
          state: billingAddress.state || undefined,
          pincode: billingAddress.pinCode || undefined,
          country: billingAddress.country || undefined,
        },
        primary_contact: {
          name: pocDetails.poc1?.name || undefined,
          role: pocDetails.poc1?.role || undefined,
          mobile: pocDetails.poc1?.mobileNo || undefined,
          email: pocDetails.poc1?.email || undefined,
        },
        secondary_contact: pocDetails.poc2?.name ? {
          name: pocDetails.poc2?.name || undefined,
          role: pocDetails.poc2?.role || undefined,
          mobile: pocDetails.poc2?.mobileNo || undefined,
          email: pocDetails.poc2?.email || undefined,
        } : undefined,
        metadata: {
          bank_name: bankDetails.bankName || undefined,
          branch_address: bankDetails.branchAddress || undefined,
          account_no: bankDetails.accountNo || undefined,
          ifsc_no: bankDetails.ifscNo || undefined,
        },
      };

      console.log('Customer API: Creating customer with payload:', payload);

      // Step 1: Create customer
      const createResult = await customerApi.createCustomer(payload);

      if (!createResult.success) {
        setFeedback({
          success: '',
          error: createResult.message || 'Failed to create customer. Please try again.',
        });
        return;
      }

      const customerId = createResult.customer?.customer_id;

      if (!customerId) {
        setFeedback({
          success: '',
          error: 'Customer created but customer ID not returned. Please contact support.',
        });
        return;
      }

      // Step 2: Upload logo if provided
      if (logoData && hasLogo && logoPreview) {
        try {
          // Get file type from logoPreview (base64 data URL)
          const base64Match = logoPreview.match(/^data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+);base64,/);
          const contentType = base64Match ? base64Match[1] : 'image/jpeg';

          console.log('Customer API: Uploading logo with content type:', contentType);

          // Get presigned URL
          const presignResult = await customerApi.presignLogoUpload(customerId, contentType);

          if (!presignResult.success) {
            console.warn('Failed to get presigned URL for logo:', presignResult.message);
            // Continue without logo - customer is already created
          } else {
            // Convert base64 to Blob
            // logoData is already base64 without data URL prefix (from handleLogoUpload)
            // But logoPreview has the full data URL, so use that
            const base64Data = logoPreview.includes(',') ? logoPreview.split(',')[1] : logoData;
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: contentType });

            // Upload to S3 using presigned POST
            const s3FormData = new FormData();
            Object.entries(presignResult.presigned_post.fields).forEach(([key, value]) => {
              s3FormData.append(key, value);
            });
            s3FormData.append('file', blob);

            console.log('Customer API: Uploading to S3:', presignResult.presigned_post.url);

            const uploadResponse = await fetch(presignResult.presigned_post.url, {
              method: 'POST',
              body: s3FormData,
            });

            if (!uploadResponse.ok) {
              const uploadErrorText = await uploadResponse.text();
              console.warn('Failed to upload logo to S3:', uploadResponse.status, uploadErrorText);
              // Continue without logo - customer is already created
            } else {
              console.log('Customer API: Logo uploaded successfully, updating customer with logo_key');
              // Update customer with logo key
              const updateResult = await customerApi.updateCustomer(customerId, {
                logo_key: presignResult.object_key,
                logo_content_type: contentType,
              });

              if (!updateResult.success) {
                console.warn('Failed to update customer with logo key:', updateResult.message);
                // Continue - logo is uploaded but key not saved
              }
            }
          }
        } catch (logoError) {
          console.error('Error uploading logo:', logoError);
          // Continue - customer is already created, logo upload is optional
        }
      }

      // Success
      setFeedback({
        success: 'Customer onboarded successfully!',
        error: '',
      });

      // Reset form after successful submission
      setTimeout(() => {
        setCustomerName('');
        setBillingAddress({
          email: '',
          address: '',
          country: '',
          pinCode: '',
          city: '',
          state: '',
        });
        setBankDetails({
          panNumber: '',
          gstNumber: '',
          bankName: '',
          branchAddress: '',
          accountNo: '',
          ifscNo: '',
        });
        setPocDetails({
          poc1: createEmptyPoc(),
          poc2: createEmptyPoc(),
        });
        setSelectedSites([]);
        setLogoData(null);
        setLogoPreview(null);
        setHasLogo(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        // Clear success message when form is reset
        setFeedback({ success: '', error: '' });
      }, 2000);
    } catch (error) {
      console.error('Error submitting customer:', error);
      setFeedback({
        success: '',
        error: error.message || 'Failed to onboard customer. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSiteSelectionConfirm = (selectedIds) => {
    const selectedSiteData = sites.filter((site) =>
      selectedIds.includes(site.site_id || site.id)
    );
    setSelectedSites(selectedSiteData);
    setFeedback({ success: '', error: '' });
  };

  const handleRemoveSite = (siteId) => {
    setSelectedSites((prev) => prev.filter((site) => (site.site_id || site.id) !== siteId));
    setFeedback({ success: '', error: '' });
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size before processing (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setFeedback({
        success: '',
        error: `Image file is too large (${(file.size / (1024 * 1024)).toFixed(2)} MB). Maximum allowed size is 5MB.`,
      });
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      setFeedback({ success: '', error: 'Please select a valid image file.' });
      return;
    }

    try {
      setFeedback({ success: '', error: '' });

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result.split(',')[1];
        setLogoData(base64);
        setLogoPreview(e.target.result);
        setHasLogo(true);
        setFeedback({ success: '', error: '' });
      };

      reader.onerror = () => {
        setFeedback({ success: '', error: 'Failed to read image file. Please try again.' });
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing image:', error);
      setFeedback({ success: '', error: 'Failed to process image. Please try again.' });
    }
  };

  const handleRemoveLogo = () => {
    if (logoPreview && logoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoData(null);
    setLogoPreview(null);
    setHasLogo(false);
    setFeedback({ success: '', error: '' });
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const renderPocSection = (pocKey, title) => {
    const data = pocDetails[pocKey];
    const isPoc1 = pocKey === 'poc1';
    const displayTitle = isPoc1 ? title : `${title} (optional)`;
    
    return (
      <div className="space-y-4 rounded-2xl border border-white/30 bg-white/40 p-4 shadow-sm backdrop-blur">
        <h3 className="text-base font-semibold text-gray-900">{displayTitle}</h3>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="mb-1 block text-sm text-gray-600">
              Name {isPoc1 && <span className="text-red-500">*</span>}
            </label>
            <Input
              type="text"
              value={data.name}
              onChange={(e) => handlePocChange(pocKey, 'name', e.target.value)}
              placeholder="Enter name"
              className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-600">
              Role {isPoc1 && <span className="text-red-500">*</span>}
            </label>
            <Input
              type="text"
              value={data.role}
              onChange={(e) => handlePocChange(pocKey, 'role', e.target.value)}
              placeholder="Enter role"
              className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-600">
              Mobile No. {isPoc1 && <span className="text-red-500">*</span>}
            </label>
            <Input
              type="tel"
              value={data.mobileNo}
              onChange={(e) => handlePocChange(pocKey, 'mobileNo', e.target.value)}
              placeholder="+91 98765 43210"
              className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-600">
              Email {isPoc1 && <span className="text-red-500">*</span>}
            </label>
            <Input
              type="email"
              value={data.email}
              onChange={(e) => handlePocChange(pocKey, 'email', e.target.value)}
              placeholder="name@company.com"
              className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto">
        <header className="mb-8 text-center">
          <h1 className="text-3xl font-semibold text-gray-900">Customer Onboarding</h1>
          <p className="mt-2 text-gray-500">
            Add a new customer to the system with complete details.
          </p>
        </header>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Row: Logo Upload (320px) + Customer Information (flex) */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Column 1: Logo Upload Section - Fixed 320px */}
            <div className="w-full lg:w-[320px] flex-shrink-0">
              <Card.Gradient className="p-6">
                <div className="text-center">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Company Logo (optional)
                  </h2>

                  <div className="flex flex-col items-center gap-4">
                    {/* Logo Preview */}
                    <div className="relative">
                      {logoPreview ? (
                        <div className="w-32 h-32 rounded-xl border-4 border-gray-300 overflow-hidden bg-white">
                          <img
                            src={logoPreview}
                            alt="Company Logo Preview"
                            className="w-full h-full object-contain"
                          />
                          <button
                            type="button"
                            onClick={handleRemoveLogo}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                            style={{ fontSize: '20px', lineHeight: '1' }}
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <div className="w-32 h-32 rounded-xl border-4 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                          <Camera className="w-12 h-12 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Upload Button */}
                    <div className="flex gap-4">
                      <Button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                        style={{ color: '#ffffff' }}
                      >
                        <Upload className="w-4 h-4" style={{ color: '#ffffff' }} />
                        Upload Logo
                      </Button>
                    </div>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                  </div>
                </div>
              </Card.Gradient>
            </div>

            {/* Column 2: Customer Information Section - Flex */}
            <div className="flex-1">
              <Card.Gradient className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h2>
                <div>
                  <label className="mb-1 block text-sm text-gray-600">Customer Name(Company Name) <span className="text-red-500">*</span></label>
                  <Input
                    type="text"
                    value={customerName}
                    onChange={(e) => {
                      setCustomerName(e.target.value);
                      setFeedback({ success: '', error: '' });
                    }}
                    placeholder="Enter Customer name"
                    className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                  />
                </div>
            <div className="mt-6 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700"> Select Sites <span className="text-red-500">*</span></p>
                <Button
                  type="button"
                  onClick={() => setShowSiteDialog(true)}
                  disabled={isLoadingSites || sites.length === 0}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl"
                >
                  {isLoadingSites ? 'Loading...' : 'Select Sites'}
                </Button>
              </div>
              {siteError && <p className="text-sm text-red-600">{siteError}</p>}
              {!isLoadingSites && sites.length === 0 && (
                <p className="text-sm text-gray-500">No sites available to select.</p>
              )}
              {selectedSites.length > 0 ? (
                <div className="space-y-2">
                  {selectedSites.map((site) => (
                    <div
                      key={site.site_id || site.id}
                      className="flex items-start justify-between rounded-xl border border-gray-200 bg-white/60 p-3"
                    >
                      <div>
                        <p className="font-semibold text-gray-900">{site.site_name || 'Unknown Site'}</p>
                        <p className="text-xs text-gray-500">
                          {site.address || 'Location not specified'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveSite(site.site_id || site.id)}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No sites selected yet.</p>
              )}
            </div>
              </Card.Gradient>
            </div>
          </div>

          {/* Billing Address Section */}
          <Card.Gradient className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Billing Address</h2>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm text-gray-600">Email Address <span className="text-red-500">*</span></label>
                <Input
                  type="email"
                  value={billingAddress.email}
                  onChange={(e) => handleBillingAddressChange('email', e.target.value)}
                  placeholder="name@company.com"
                  className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-600">Address <span className="text-red-500">*</span></label>
                <Input
                  type="text"
                  value={billingAddress.address}
                  onChange={(e) => handleBillingAddressChange('address', e.target.value)}
                  placeholder="Street, Building, Area"
                  className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm text-gray-600">Country <span className="text-red-500">*</span></label>
                  <Input
                    type="text"
                    value={billingAddress.country}
                    onChange={(e) => handleBillingAddressChange('country', e.target.value)}
                    placeholder="India"
                    className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-600">Pin Code <span className="text-red-500">*</span></label>
                  <Input
                    type="text"
                    value={billingAddress.pinCode}
                    onChange={(e) => handleBillingAddressChange('pinCode', e.target.value)}
                    placeholder="500001"
                    maxLength={10}
                    className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm text-gray-600">City <span className="text-red-500">*</span></label>
                  <Input
                    type="text"
                    value={billingAddress.city}
                    onChange={(e) => handleBillingAddressChange('city', e.target.value)}
                    placeholder="Hyderabad"
                    className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-600">State <span className="text-red-500">*</span></label>
                  <Input
                    type="text"
                    value={billingAddress.state}
                    onChange={(e) => handleBillingAddressChange('state', e.target.value)}
                    placeholder="Telangana"
                    className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                  />
                </div>
              </div>
            </div>
          </Card.Gradient>

          {/* Bank Details Card */}
          <Card.Gradient className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Financial Details</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm text-gray-600">PAN Number <span className="text-red-500">*</span></label>
                  <Input
                    type="text"
                    value={bankDetails.panNumber}
                    onChange={(e) => handleBankDetailsChange('panNumber', e.target.value.toUpperCase())}
                    placeholder="ABCDE1234F"
                    maxLength={10}
                    className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-600">GST Number <span className="text-red-500">*</span></label>
                  <Input
                    type="text"
                    value={bankDetails.gstNumber}
                    onChange={(e) => handleBankDetailsChange('gstNumber', e.target.value.toUpperCase())}
                    placeholder="22AAAAA0000A1Z5"
                    maxLength={15}
                    className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-600">Bank Name <span className="text-red-500">*</span></label>
                <Input
                  type="text"
                  value={bankDetails.bankName}
                  onChange={(e) => handleBankDetailsChange('bankName', e.target.value)}
                  placeholder="Enter bank name"
                  className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-600">Branch Address <span className="text-red-500">*</span></label>
                <Input
                  type="text"
                  value={bankDetails.branchAddress}
                  onChange={(e) => handleBankDetailsChange('branchAddress', e.target.value)}
                  placeholder="Enter branch address"
                  className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm text-gray-600">Account No. <span className="text-red-500">*</span></label>
                  <Input
                    type="text"
                    value={bankDetails.accountNo}
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/\D/g, '');
                      handleBankDetailsChange('accountNo', numericValue);
                    }}
                    placeholder="Enter account number"
                    className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-600">IFSC No. <span className="text-red-500">*</span></label>
                  <Input
                    type="text"
                    value={bankDetails.ifscNo}
                    onChange={(e) => handleBankDetailsChange('ifscNo', e.target.value.toUpperCase())}
                    placeholder="Enter IFSC code"
                    maxLength={11}
                    className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                  />
                </div>
              </div>
            </div>
          </Card.Gradient>

          {/* POC Details Card */}
          <Card.Gradient className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Point of Contact Details</h2>
            <div className="grid gap-6 md:grid-cols-2">
              {renderPocSection('poc1', 'POC 1 Details')}
              {renderPocSection('poc2', 'POC 2 Details')}
            </div>
          </Card.Gradient>

          {/* Feedback Messages */}
          {feedback.error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {feedback.error}
            </div>
          )}
          {feedback.success && (
            <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              {feedback.success}
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end">
            <PrimaryButton type="submit" disabled={!isFormValid || isSubmitting}>
              {isSubmitting ? 'Onboarding...' : 'Onboard Customer'}
            </PrimaryButton>
          </div>
        </form>
      </div>

      {showSiteDialog &&
        createPortal(
          <MultiSelectDialog
            isOpen={showSiteDialog}
            onClose={() => setShowSiteDialog(false)}
            onConfirm={(selectedIds) => {
              handleSiteSelectionConfirm(selectedIds);
              setShowSiteDialog(false);
            }}
            entityType="site"
            dialogTitle="Select Site"
            availableEntities={sites}
            selectedEntityIds={selectedSites.map((site) => site.site_id || site.id)}
          />,
          document.body
        )}
    </div>
  );
};

export default CustomerOnboarding;
